import { useState } from "react";
import { ChevronDown, Loader2, MoveDown, Trash2 } from "lucide-react";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "web-design-system";
import { handleError } from "web-core/utils";
import {
  useDeletePortfolioReviewLine,
  useUpdatePortfolioReviewLine,
} from "creso-core/hooks";
import {
  PortfolioReview,
  PortfolioReviewAction,
  PortfolioReviewLine,
} from "creso-core/types";
import { convertToCurrency, formatAsPercentage } from "creso-core/utils";
import {
  BUCKET_GROUPS,
  ColumnOption,
  HOLDING_COLUMNS,
  HoldingColumnKey,
} from "./holding-columns";
import { FundCell, QuartileBadge, SourceBadge } from "./fund-badges";
import { LineCommentPopover } from "./line-comment-popover";
import { releasedAmount } from "./pick-funds-dialog";
import { SwitchFundDialog } from "./switch-fund-dialog";

const LINE_ACTIONS = ["HOLD", "SWITCH", "EXIT"] as const;

const ACTION_LABEL: Record<(typeof LINE_ACTIONS)[number], string> = {
  HOLD: "Hold",
  SWITCH: "Switch",
  EXIT: "Exit",
};

// Pinned to the right so actions stay reachable when the table scrolls.
const ACTION_CELL = "sticky right-0 bg-background text-right";

export function HoldingsTable({
  review,
  visibleColumns,
  onRecommendFunds,
}: {
  review: PortfolioReview;
  visibleColumns: HoldingColumnKey[];
  onRecommendFunds: () => void;
}) {
  const columns = HOLDING_COLUMNS.filter((c) => visibleColumns.includes(c.key));
  const readOnly = review.status !== "DRAFT";
  // Money freed by exits is offered for re-allocation under new additions.
  const available = readOnly ? 0 : releasedAmount(review);
  const groups = BUCKET_GROUPS.map((group) => ({
    ...group,
    lines: review.lines
      .filter((line) => line.bucket === group.bucket)
      .sort((a, b) => a.sort_order - b.sort_order),
  })).filter(
    (group) =>
      group.lines.length || (group.bucket === "NEW_ADDITION" && available > 0)
  );

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-200 text-sm">
        <thead className="bg-card sticky top-0 z-10">
          <tr>
            <th className="p-4 text-left font-semibold">Fund Name</th>
            {columns.map((column) => (
              <th
                key={column.key}
                className={cn(
                  "whitespace-nowrap p-4 font-semibold",
                  column.numeric ? "text-right" : "text-left"
                )}
              >
                {column.label}
              </th>
            ))}
            <th
              className={cn(
                ACTION_CELL,
                "bg-card py-4 pl-4 pr-13 font-semibold"
              )}
            >
              Action
            </th>
          </tr>
        </thead>
        {groups.map((group) => (
          <BucketGroup
            key={group.bucket}
            label={group.label}
            colSpan={columns.length + 2}
          >
            {group.bucket === "NEW_ADDITION" && available > 0 ? (
              <tr className="border-t border-border">
                <td colSpan={columns.length + 2} className="p-4">
                  <div className="flex items-center justify-between gap-4 rounded-lg bg-card px-4 py-3">
                    <span className="text-muted-foreground">
                      <span className="font-semibold text-foreground">
                        {convertToCurrency(available)}
                      </span>{" "}
                      available to re-allocate
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onRecommendFunds}
                    >
                      Recommend funds
                    </Button>
                  </div>
                </td>
              </tr>
            ) : null}
            {group.lines.map((line) => (
              <HoldingRow
                key={line.id}
                reviewId={review.id}
                line={line}
                columns={columns}
                readOnly={readOnly}
              />
            ))}
          </BucketGroup>
        ))}
      </table>
    </div>
  );
}

function BucketGroup({
  label,
  colSpan,
  children,
}: {
  label: string;
  colSpan: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <tbody className="border-t border-border">
      <tr>
        <td colSpan={colSpan} className="p-0">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="flex w-full items-center justify-between p-4 font-semibold"
          >
            {label}
            <ChevronDown
              className={cn(
                "size-4 transition-transform",
                open && "rotate-180"
              )}
            />
          </button>
        </td>
      </tr>
      {open ? children : null}
    </tbody>
  );
}

function HoldingRow({
  reviewId,
  line,
  columns,
  readOnly,
}: {
  reviewId: number;
  line: PortfolioReviewLine;
  columns: ColumnOption<HoldingColumnKey>[];
  readOnly: boolean;
}) {
  const [switching, setSwitching] = useState(false);
  const isNew = line.bucket === "NEW_ADDITION";
  const isSwitch = line.bucket === "SWITCH";
  const xirr = line.xirr === null ? null : Number(line.xirr);

  const cells: Record<HoldingColumnKey, React.ReactNode> = {
    source: <SourceBadge label={line.source_label} />,
    quartile: (
      <QuartileBadge quartile={isNew ? line.target_quartile : line.quartile} />
    ),
    xirr:
      xirr === null ? (
        "-"
      ) : (
        <span className={xirr >= 0 ? "text-constructive" : "text-destructive"}>
          {formatAsPercentage(xirr)}
        </span>
      ),
    invested: isNew ? "-" : convertToCurrency(line.invested_value),
    current: isNew
      ? convertToCurrency(line.amount ?? undefined)
      : convertToCurrency(line.current_value),
    weight: formatAsPercentage(line.weight_pct, "-", 1),
    stcg: convertToCurrency(line.stcg),
    ltcg: convertToCurrency(line.ltcg),
  };

  // Funds leaving the portfolio are muted; a switch's target sits below.
  const muted = (isSwitch || line.bucket === "EXIT") && "opacity-50";

  return (
    <>
      <tr className="border-t border-border">
        <td className={cn("px-4 py-3", muted)}>
          {isNew ? (
            <FundCell
              name={line.target_scheme_name}
              category={line.target_category}
              amcCode={line.target_amc_morningstar_code}
              quartile={line.target_quartile}
            />
          ) : (
            <FundCell
              name={line.source_scheme_name}
              category={line.source_category}
              amcCode={line.amc_morningstar_code}
              quartile={line.quartile}
            />
          )}
        </td>
        {columns.map(({ key, numeric }) => (
          <td
            key={key}
            className={cn(
              "whitespace-nowrap px-4 py-3",
              numeric && "text-right",
              muted
            )}
          >
            {cells[key]}
          </td>
        ))}
        <td className={cn(ACTION_CELL, "py-3 pl-4 pr-2")}>
          <div className="flex items-center justify-end gap-2">
            {isNew ? (
              <RemoveLineButton
                reviewId={reviewId}
                line={line}
                readOnly={readOnly}
              />
            ) : (
              <LineActionSelect
                reviewId={reviewId}
                line={line}
                readOnly={readOnly}
                onSwitch={() => setSwitching(true)}
              />
            )}
            <LineCommentPopover
              reviewId={reviewId}
              line={line}
              readOnly={readOnly}
            />
          </div>
        </td>
      </tr>
      {isSwitch ? (
        <SwitchTargetRow
          line={line}
          columns={columns}
          readOnly={readOnly}
          onChange={() => setSwitching(true)}
        />
      ) : null}
      {isNew ? null : (
        <SwitchFundDialog
          reviewId={reviewId}
          line={line}
          open={switching}
          onOpenChange={setSwitching}
        />
      )}
    </>
  );
}

function SwitchTargetRow({
  line,
  columns,
  readOnly,
  onChange,
}: {
  line: PortfolioReviewLine;
  columns: ColumnOption<HoldingColumnKey>[];
  readOnly: boolean;
  onChange: () => void;
}) {
  const hasTarget = !!line.target_scheme_name;
  const cells: Partial<Record<HoldingColumnKey, React.ReactNode>> = {
    source: hasTarget ? <SourceBadge label="New" /> : null,
    quartile: hasTarget ? (
      <QuartileBadge quartile={line.target_quartile} />
    ) : null,
    xirr: hasTarget ? "-" : null,
    invested: hasTarget ? "-" : null,
    current: hasTarget
      ? convertToCurrency(line.amount ?? line.current_value)
      : null,
  };

  return (
    <tr>
      {/* The arrow sits in the top padding, between the old and new fund. */}
      <td className="relative px-4 pb-3 pt-6">
        <MoveDown
          aria-hidden
          className="absolute left-6 top-1 size-4 text-muted-foreground"
        />
        {hasTarget ? (
          <FundCell
            name={line.target_scheme_name}
            category={line.target_category}
            amcCode={line.target_amc_morningstar_code}
          />
        ) : (
          <span className="pl-11 text-muted-foreground">
            No fund picked yet
          </span>
        )}
      </td>
      {columns.map(({ key, numeric }) => (
        <td
          key={key}
          className={cn(
            "whitespace-nowrap px-4 pb-3 pt-6",
            numeric && "text-right"
          )}
        >
          {cells[key]}
        </td>
      ))}
      <td className={cn(ACTION_CELL, "pb-3 pl-4 pr-2 pt-6")}>
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            className="w-36"
            disabled={readOnly}
            onClick={onChange}
          >
            {hasTarget ? "Change" : "Pick fund"}
          </Button>
          {/* Keeps the button in line with the action select above. */}
          <span aria-hidden className="size-9 shrink-0" />
        </div>
      </td>
    </tr>
  );
}

function LineActionSelect({
  reviewId,
  line,
  readOnly,
  onSwitch,
}: {
  reviewId: number;
  line: PortfolioReviewLine;
  readOnly: boolean;
  onSwitch: () => void;
}) {
  const { mutate: updateLine, isPending } = useUpdatePortfolioReviewLine();
  // A MANUAL review starts every line in HOLD with no action chosen yet.
  const value = line.action ?? "HOLD";

  const onValueChange = (action: PortfolioReviewAction) => {
    // A switch needs a target, so it is saved from the fund picker.
    if (action === "SWITCH") {
      onSwitch();
    } else {
      updateLine(
        { reviewId, lineId: line.id, action },
        { onError: (error) => handleError(error, "Could not update action") }
      );
    }
  };

  return (
    <Select
      value={value}
      disabled={readOnly || isPending}
      onValueChange={(action) => onValueChange(action as PortfolioReviewAction)}
    >
      <SelectTrigger className="w-36 bg-background" aria-label="Action">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LINE_ACTIONS.map((action) => (
          <SelectItem key={action} value={action}>
            {ACTION_LABEL[action]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function RemoveLineButton({
  reviewId,
  line,
  readOnly,
}: {
  reviewId: number;
  line: PortfolioReviewLine;
  readOnly: boolean;
}) {
  const { mutate: deleteLine, isPending } = useDeletePortfolioReviewLine();
  return (
    <Button
      variant="outline"
      className="w-36"
      disabled={readOnly || isPending}
      onClick={() =>
        deleteLine(
          { reviewId, lineId: line.id },
          {
            onError: (error) => handleError(error, "Could not remove the fund"),
          }
        )
      }
    >
      {isPending ? <Loader2 className="animate-spin" /> : <Trash2 />}
      Remove
    </Button>
  );
}
